import {
  addMonths,
  addYears,
  format,
  isBefore,
  isValid,
  subMonths,
  subYears,
} from "date-fns";
import { ChevronIcon } from "vega-icons";

import { Button } from "@vega-core/components/shadcn/button";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { type CalendarView } from "../hooks/types";

export const DEFAULT_MAX_FUTURE_YEARS = 50;

type Props = {
  view: CalendarView;
  displayMonth: Date;
  onSelect: (date: Date) => void;
  onNavigate: (date: Date) => void;
  name: string;
  isBirthday?: boolean;
  maxFutureYears?: number;
  minDate?: Date;
  shouldDisableDate?: (date: Date) => boolean;
};

const normalizeDate = (
  input: Date | number | string | null | undefined,
): Date | undefined => {
  if (input == null) return undefined;

  const date = input instanceof Date ? input : new Date(input);

  return isValid(date) ? date : undefined;
};

export function MonthYearView({
  view,
  displayMonth,
  onSelect,
  onNavigate,
  name,
  isBirthday = false,
  maxFutureYears = DEFAULT_MAX_FUTURE_YEARS,
  minDate,
  shouldDisableDate,
}: Props) {
  const handlePrevious = () => {
    if (view === "MONTH") {
      onNavigate(subMonths(displayMonth, 12));
    } else {
      onNavigate(subYears(displayMonth, 12));
    }
  };

  const handleNext = () => {
    if (view === "MONTH") {
      onNavigate(addMonths(displayMonth, 12));
    } else {
      onNavigate(addYears(displayMonth, 12));
    }
  };

  const renderCaption = () => {
    if (view === "MONTH") {
      return format(displayMonth, "yyyy");
    } else {
      const currentYear = new Date().getFullYear();
      const startYear = Math.floor(displayMonth.getFullYear() / 12) * 12;
      const maxYear = isBirthday ? currentYear : currentYear + maxFutureYears;
      const endYear = Math.min(startYear + 11, maxYear);

      return `${startYear} - ${endYear}`;
    }
  };

  const isLastYearGroup = () => {
    if (view === "YEAR") {
      const currentYear = new Date().getFullYear();
      const startYear = Math.floor(displayMonth.getFullYear() / 12) * 12;
      const maxYear = isBirthday ? currentYear : currentYear + maxFutureYears;

      return startYear + 11 >= maxYear;
    }

    return false;
  };

  const isDateDisabled = (date: Date): boolean => {
    if (shouldDisableDate && shouldDisableDate(date)) {
      return true;
    }

    const normalizedMinDate = normalizeDate(minDate);

    if (!normalizedMinDate) {
      return false;
    }

    if (view === "MONTH") {
      const dateYear = date.getFullYear();
      const dateMonth = date.getMonth();
      const minYear = normalizedMinDate.getFullYear();
      const minMonth = normalizedMinDate.getMonth();

      return (
        dateYear < minYear || (dateYear === minYear && dateMonth < minMonth)
      );
    }

    if (view === "YEAR") {
      return date.getFullYear() < normalizedMinDate.getFullYear();
    }

    return isBefore(date, normalizedMinDate);
  };

  const renderGrid = () => {
    if (view === "MONTH") {
      return Array.from({ length: 12 }, (_, i) => {
        const month = new Date(displayMonth.getFullYear(), i);
        const isDisabled = isDateDisabled(month);

        return (
          <Button
            data-testid={`${name}-month-${i}`}
            disabled={isDisabled}
            key={i}
            onClick={() => onSelect(month)}
            variant="ghost"
          >
            {format(month, "MMM")}
          </Button>
        );
      });
    } else {
      const currentYear = new Date().getFullYear();
      const startYear = Math.floor(displayMonth.getFullYear() / 12) * 12;
      const maxYear = isBirthday ? currentYear : currentYear + maxFutureYears;
      const endYear = Math.min(startYear + 11, maxYear);
      const years = Array.from(
        { length: endYear - startYear + 1 },
        (_, i) => startYear + i,
      );

      return years.map((year) => {
        const yearDate = new Date(year, displayMonth.getMonth(), 1);
        const isDisabled = isDateDisabled(yearDate);

        return (
          <Button
            data-testid={`${name}-year-${year}`}
            disabled={isDisabled}
            key={year}
            onClick={() => onSelect(yearDate)}
            variant="ghost"
          >
            {year}
          </Button>
        );
      });
    }
  };

  return (
    <div className="p-3">
      <div className="flex justify-center pt-1 relative items-center">
        <Button
          className="absolute left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          data-testid={`${name}-previous`}
          onClick={handlePrevious}
          size="icon"
          variant="outline"
        >
          <ChevronIcon className="h-4 w-4 rotate-90 " />
        </Button>
        <div className="body-semi-large-semibold">{renderCaption()}</div>
        <Button
          className={cnTwMerge(
            "absolute right-1 h-7 w-7 bg-transparent p-0",
            isLastYearGroup()
              ? "opacity-30 cursor-not-allowed"
              : "opacity-50 hover:opacity-100",
          )}
          data-testid={`${name}-next`}
          disabled={isLastYearGroup()}
          onClick={handleNext}
          size="icon"
          variant="outline"
        >
          <ChevronIcon className="h-4 w-4 -rotate-90" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">{renderGrid()}</div>
    </div>
  );
}
